import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MethodologyPanel = ({ methodology, isExpanded, onToggle }) => {
  const [activeFramework, setActiveFramework] = useState(0);

  return (
    <div className={`bg-card rounded-xl border border-border transition-all duration-500 ${
      isExpanded ? 'shadow-lg cosmic-glow' : 'hover:shadow-md'
    }`}>
      {/* Panel Header */}
      <div 
        className="p-6 cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center transition-colors ${
              isExpanded ? 'bg-accent text-background' : 'bg-surface text-accent'
            }`}>
              <Icon name={methodology?.icon} size={24} />
            </div>
            <div>
              <h3 className="font-headline font-bold text-xl text-foreground">
                {methodology?.title}
              </h3>
              <p className="font-body text-sm text-muted-foreground">
                {methodology?.subtitle}
              </p>
            </div>
          </div>
          <Icon 
            name={isExpanded ? "ChevronUp" : "ChevronDown"} 
            size={20} 
            className="text-muted-foreground transition-transform duration-300"
          />
        </div>
      </div>
      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-6 pb-6 space-y-6">
          {/* Overview */}
          <div className="p-4 bg-surface/30 rounded-lg">
            <p className="font-body text-sm text-foreground leading-relaxed">
              {methodology?.overview}
            </p>
          </div>

          {/* Frameworks Tabs */}
          <div>
            <div className="flex space-x-1 mb-4 bg-surface/30 rounded-lg p-1">
              {methodology?.frameworks?.map((framework, index) => (
                <button
                  key={index}
                  className={`flex-1 px-4 py-2 rounded-md font-body font-medium text-sm transition-all duration-200 ${
                    activeFramework === index
                      ? 'bg-accent text-background' :'text-muted-foreground hover:text-foreground'
                  }`}
                  onClick={() => setActiveFramework(index)}
                >
                  {framework?.name}
                </button>
              ))}
            </div>

            {/* Active Framework Content */}
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Icon 
                  name={methodology?.frameworks?.[activeFramework]?.icon} 
                  size={20} 
                  className="text-accent" 
                />
                <h4 className="font-headline font-bold text-lg text-foreground">
                  {methodology?.frameworks?.[activeFramework]?.name}
                </h4>
              </div>

              <p className="font-body text-sm text-muted-foreground">
                {methodology?.frameworks?.[activeFramework]?.description}
              </p>

              {/* Framework Steps */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {methodology?.frameworks?.[activeFramework]?.steps?.map((step, index) => (
                  <div key={index} className="flex items-start space-x-3 p-3 rounded-lg bg-surface/20">
                    <div className="w-6 h-6 bg-accent/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-accent">{index + 1}</span>
                    </div>
                    <div>
                      <h5 className="font-body font-medium text-sm text-foreground mb-1">
                        {step?.title}
                      </h5>
                      <p className="font-body text-xs text-muted-foreground">
                        {step?.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Tools Used */}
              <div>
                <h5 className="font-body font-semibold text-sm text-foreground mb-3">
                  Tools & Resources
                </h5>
                <div className="flex flex-wrap gap-2">
                  {methodology?.frameworks?.[activeFramework]?.tools?.map((tool, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-accent/10 text-accent text-xs rounded-full font-body border border-accent/20"
                    >
                      {tool}
                    </span>
                  ))}
                </div>
              </div>

              {/* Success Metrics */}
              {methodology?.frameworks?.[activeFramework]?.metrics && (
                <div className="grid grid-cols-3 gap-4 pt-4 border-t border-border/20">
                  {methodology?.frameworks?.[activeFramework]?.metrics?.map((metric, index) => (
                    <div key={index} className="text-center">
                      <div className="font-headline font-bold text-lg text-accent">
                        {metric?.value}
                      </div>
                      <div className="font-body text-xs text-muted-foreground">
                        {metric?.label}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Action Button */}
          <div className="pt-4 border-t border-border/20">
            <Button
              variant="outline"
              size="sm"
              iconName="ExternalLink"
              iconPosition="right"
              className="border-accent/30 text-accent hover:bg-accent/10"
            >
              Learn More About This Approach
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MethodologyPanel;